import { useMemo } from 'react';

import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { NonEmptyDateRange } from '@packages/fenrir/components/date-pickers';
import {
  type DateRange,
  type PickersShortcutsItem,
} from '@packages/fenrir/components/date-pickers';

export enum DatePickerShortcuts {
  Today = 'today',
  Past24 = 'past24',
  Past72 = 'past72',
  PastHour = 'pastHour',
  PastWeek = 'pastWeek',
  Past30Days = 'past30Days',
  Past90days = 'past90Days',
  Past12Months = 'past12Months',
}

export const dateRangeGenerators: {
  [key in DatePickerShortcuts]: (today: Dayjs) => NonEmptyDateRange<Dayjs>;
} = {
  [DatePickerShortcuts.Today]: (today) => [
    today.startOf('day'),
    today.endOf('day'),
  ],
  [DatePickerShortcuts.PastHour]: (today) => [
    today.millisecond(0).subtract(1, 'hour'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.Past24]: (today) => [
    today.millisecond(0).subtract(24, 'hour'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.Past72]: (today) => [
    today.millisecond(0).subtract(72, 'hour'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.PastWeek]: (today) => [
    today.millisecond(0).subtract(7, 'day'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.Past30Days]: (today) => [
    today.millisecond(0).subtract(30, 'day'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.Past90days]: (today) => [
    today.millisecond(0).subtract(90, 'day'),
    today.millisecond(0),
  ],
  [DatePickerShortcuts.Past12Months]: (today) => [
    today.millisecond(0).subtract(365, 'day'),
    today.millisecond(0),
  ],
};

export const useDatePickerShortcuts = (
  shortcutKeys?: DatePickerShortcuts[],
): PickersShortcutsItem<DateRange<Dayjs>>[] => {
  const { t } = useTranslation('datePicker');

  return useMemo(() => {
    if (!shortcutKeys) return [];

    return shortcutKeys.map((shortcutKey) => ({
      id: shortcutKey,
      label: t(shortcutKey),
      getValue: () => {
        const today = dayjs();
        const range = dateRangeGenerators[shortcutKey](today);

        return range;
      },
    }));
  }, [shortcutKeys, t]);
};
